/**
 * Strict npm package-name validator.
 *
 * Stricter than the historical `NPM_PACKAGE_RE` because that regex accepted
 * any name shaped like `[a-z0-9-_~.]+` — long enough to slip CSS content,
 * shell metacharacters, and other garbage into `bun add`. This helper
 * enforces npm's actual rules from
 * https://docs.npmjs.com/cli/v10/configuring-npm/package-json#name and
 * mirrors the validate-npm-package-name package's constraints, minus the
 * builtin/blacklist set which we keep small (just the names that show up
 * frequently in LLM hallucinations).
 *
 * Returns `true` only for strings that bun/npm will accept as installable
 * package specifiers. Anything else — empty, too long, leading dot, spaces,
 * embedded CSS — returns `false` so callers can drop it.
 */

// 214 chars is npm's published cap; matches validate-npm-package-name.
const MAX_PACKAGE_NAME_LENGTH = 214;

// Names npm itself refuses, plus a couple of LLM-favourite junk values.
const BLOCKED_NAMES = new Set(['node_modules', 'favicon.ico', '.', '..']);

// Scoped: @scope/name. Unscoped: name. Charset matches the legacy regex but
// is re-asserted here so the helper is self-contained.
const PACKAGE_NAME_RE =
  /^(?:@[a-z0-9-~][a-z0-9-._~]*\/)?[a-z0-9-~][a-z0-9-._~]*$/;

// Plain `boolean` return rather than a `name is string` type guard. The
// type guard tripped strict-mode CI: callers that already had a `string`
// passed to it (the common case) saw the negative branch narrow to `never`,
// breaking subsequent `.length` / property access. Validity is what callers
// care about; type narrowing in the positive branch isn't load-bearing.
export function isValidNpmPackageName(name: unknown): boolean {
  if (typeof name !== 'string') return false;
  if (name.length === 0 || name.length > MAX_PACKAGE_NAME_LENGTH) return false;
  if (BLOCKED_NAMES.has(name)) return false;
  // Fast reject for anything that's clearly not a package name. The regex
  // below would catch most of these, but keeping these checks explicit
  // makes the intent obvious and saves a regex test on the obvious losers.
  if (name.startsWith('.') || name.startsWith('_')) return false;
  if (/\s/.test(name)) return false;
  return PACKAGE_NAME_RE.test(name);
}
